
import React, { useState, useEffect, useMemo } from 'react';
import { useLocation, Link, useNavigate } from 'react-router-dom';
import { Calendar, Clock, User, Mail, FileText, Send, ChevronLeft, Info, Loader2, HelpCircle, AlertCircle } from 'lucide-react';
import Swal from 'sweetalert2';
import { apiRequest } from '../services/api';
import { PublicData, GlobalSettings } from '../types';
import { FormSkeleton } from './Skeletons';

interface BookingFormProps { settings: GlobalSettings; }

export const BookingForm: React.FC<BookingFormProps> = ({ settings }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const initialDate = (location.state as { date?: string })?.date || '';
  
  const [formData, setFormData] = useState({ 
    name: '', 
    email: '', 
    date: initialDate, 
    time: '', 
    topic: '', 
    description: '' 
  });
  
  const [data, setData] = useState<PublicData | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [dateError, setDateError] = useState<string | null>(null);

  useEffect(() => {
    const init = async () => {
      setLoading(true);
      const res = await apiRequest<PublicData>('getPublic');
      if (res.success && res.data) setData(res.data);
      setLoading(false);
    };
    init();
  }, []);

  // 計算 HTML5 日期選擇器的限制範圍
  const dateLimits = useMemo(() => {
    const now = new Date();
    const minDate = new Date(now);
    minDate.setDate(now.getDate() + settings.minNotice);
    const maxDate = new Date(now);
    maxDate.setDate(now.getDate() + settings.maxAdvance);
    const toStr = (d: Date) => d.toISOString().split('T')[0];
    return { min: toStr(minDate), max: toStr(maxDate) };
  }, [settings.minNotice, settings.maxAdvance]);

  // iPhone 嚴格日期校驗：確保使用者無法選到公休或範圍外
  const checkIsBlocked = (dateStr: string) => {
    if (!dateStr) return null;
    const targetDate = new Date(dateStr);
    targetDate.setHours(0, 0, 0, 0);
    const now = new Date();
    now.setHours(0, 0, 0, 0);

    // 範圍檢查
    const diffDays = Math.ceil((targetDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
    if (diffDays < settings.minNotice) return `此日期太近，需提前 ${settings.minNotice} 天預約`;
    if (diffDays > settings.maxAdvance) return `僅開放 ${settings.maxAdvance} 天內的預約`;
    if (diffDays < 0) return '無法預約過去的日期';

    // 公休檢查
    const day = targetDate.getDay();
    const configs = settings.allConfigs.length > 0 ? settings.allConfigs : (data?.configs || []);

    for (const cfg of configs) {
      const cfgVal = (cfg.value || "").trim();
      if (cfg.type === 'WEEKDAY' && cfgVal.split(',').map(v => v.trim()).includes(String(day))) return cfg.note || '例行公休';
      if (cfg.type === 'DATE' && cfgVal === dateStr) return cfg.note || '特定公休';
      if (cfg.type === 'RANGE') {
        const [start, end] = cfgVal.split(',');
        if (dateStr >= (start || "").trim() && dateStr <= (end || "").trim()) return cfg.note || '期間休假';
      }
    }
    return null;
  };

  const handleDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    const reason = checkIsBlocked(val);
    if (reason) {
      setDateError(`不可預約：${reason}`);
      setFormData(prev => ({ ...prev, date: '', time: '' }));
    } else {
      setDateError(null);
      setFormData(prev => ({ ...prev, date: val, time: '' }));
    }
  };

  const bookedTimes = useMemo(() => {
    if (!formData.date || !data) return [];
    return data.bookings
      .filter(b => {
        const bDate = (b.date || "").toString().trim();
        const bStatus = (b.status || "").toString().toUpperCase();
        return bDate === formData.date && bStatus !== 'REJECTED';
      })
      .map(b => (b.time || "").toString().trim().slice(0, 5));
  }, [formData.date, data]);

  // 公告處理：自動加上數字序號
  const formattedNotice = useMemo(() => {
    if (!settings.notice) return [];
    return settings.notice.split('\n').map(l => l.trim()).filter(l => l.length > 0);
  }, [settings.notice]);

  // 主題處理：自動補上「其他」
  const enhancedTopicOptions = useMemo(() => {
    const options = [...settings.topicOptions];
    if (options.length > 0 && !options.includes('其他')) {
      options.push('其他');
    }
    return options;
  }, [settings.topicOptions]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name || !formData.email || !formData.date || !formData.time || !formData.topic) {
      return Swal.fire({ icon: 'warning', title: '資訊不完整', text: '請務必填寫所有標註 (*) 的欄位。' });
    }

    setSubmitting(true);
    try {
      // 確保欄位名稱正確傳送，這關係到 GAS 是否能成功發送 Email
      const res = await apiRequest('create', 'POST', formData);
      if (res.success) {
        await Swal.fire({
          icon: 'success',
          title: '預約已送出',
          text: '我們會儘速確認預約並透過 Email 或電話與您聯絡。',
          confirmButtonColor: '#2563eb'
        });
        navigate('/');
      } else {
        Swal.fire({ icon: 'error', title: '送出失敗', text: res.message || '伺服器忙碌中，請稍後再試' });
      }
    } catch (error) {
      Swal.fire({ icon: 'error', title: '網路錯誤', text: '連線異常，請確認網路狀態後再試一次' });
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <FormSkeleton />;

  const labelClass = "text-[11px] font-black text-slate-400 uppercase tracking-widest flex items-center gap-2 mb-2";
  const inputClass = "w-full p-4 bg-slate-50 border border-slate-200 rounded-2xl outline-none focus:border-blue-500 focus:bg-white transition-all font-bold text-slate-700 placeholder:text-slate-300";

  return (
    <div className="w-full max-w-3xl mx-auto pb-20 animate-fade-in">
      <div className="mb-6">
        <Link to="/" className="inline-flex items-center gap-2 text-slate-400 hover:text-blue-600 font-bold transition-colors">
          <ChevronLeft className="w-5 h-5" />
          <span>返回日曆</span>
        </Link>
      </div>

      {/* 預約須知：帶序號的公告區塊 */}
      {formattedNotice.length > 0 && (
        <div className="mb-10 bg-blue-50/50 border border-blue-100 p-8 rounded-[2.5rem] flex gap-5 shadow-sm">
          <div className="w-14 h-14 bg-blue-600 rounded-2xl flex items-center justify-center text-white shrink-0 shadow-lg shadow-blue-200">
            <Info className="w-7 h-7" />
          </div>
          <div className="flex-1">
            <h4 className="font-black text-blue-600 text-sm mb-3 uppercase tracking-[0.15em]">預約前請閱讀</h4>
            <div className="space-y-2.5">
              {formattedNotice.map((line, idx) => (
                <div key={idx} className="flex gap-2.5 items-start text-slate-600 text-sm font-bold leading-relaxed">
                  <span className="text-blue-500 font-black min-w-[1.2rem]">{idx + 1}.</span>
                  <span>{line}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      <div className="bg-white p-8 sm:p-12 rounded-[3.5rem] shadow-2xl border border-slate-100">
        <h2 className="text-2xl font-black text-slate-800 mb-10 border-l-4 border-blue-600 pl-5 tracking-tight">{settings.formTitle}</h2>

        <form onSubmit={handleSubmit} className="space-y-10">
          {/* 日期與時段 (兩欄佈局) */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
            <div className="space-y-1">
              <label className={labelClass}><Calendar className="w-3.5 h-3.5 text-blue-500" /> 預約日期 *</label>
              <input 
                type="date" 
                required
                min={dateLimits.min}
                max={dateLimits.max}
                className={`${inputClass} ${dateError ? 'border-rose-300 bg-rose-50 text-rose-600' : ''}`} 
                value={formData.date} 
                onChange={handleDateChange} 
              />
              {dateError && <p className="text-[10px] text-rose-500 font-black mt-1.5 flex items-center gap-1 uppercase tracking-tight"><AlertCircle className="w-3 h-3" /> {dateError}</p>}
            </div>
            <div className="space-y-1">
              <label className={labelClass}><Clock className="w-3.5 h-3.5 text-blue-500" /> 預約時段 *</label>
              <select 
                required
                disabled={!formData.date}
                className={inputClass} 
                value={formData.time} 
                onChange={e => setFormData({...formData, time: e.target.value})}
              >
                <option value="">選擇時段</option>
                {settings.timeSlots.map(t => {
                  const norm = t.toString().trim().slice(0, 5);
                  const isTaken = bookedTimes.includes(norm);
                  return <option key={t} value={t} disabled={isTaken}>{t} {isTaken ? '(此段已滿)' : ''}</option>;
                })}
              </select>
            </div>
          </div>

          {/* 姓名與聯絡資訊 (兩欄佈局) */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
            <div className="space-y-1">
              <label className={labelClass}><User className="w-3.5 h-3.5 text-blue-500" /> 申請人姓名 *</label>
              <input 
                type="text" 
                required
                className={inputClass} 
                placeholder="您的姓名"
                value={formData.name} 
                onChange={e => setFormData({ ...formData, name: e.target.value })} 
              />
            </div>
            <div className="space-y-1">
              <label className={labelClass}><Mail className="w-3.5 h-3.5 text-blue-500" /> 電子郵件或聯絡電話 *</label>
              <input 
                type="text" 
                required
                className={inputClass} 
                placeholder="Email 或 手機號碼"
                value={formData.email} 
                onChange={e => setFormData({ ...formData, email: e.target.value })} 
              />
            </div>
          </div>

          {/* 諮詢主題 */}
          <div className="space-y-1">
            <label className={labelClass}><HelpCircle className="w-3.5 h-3.5 text-blue-500" /> 諮詢項目 / 主題 *</label>
            {enhancedTopicOptions.length > 0 ? (
              <select 
                required
                className={inputClass} 
                value={formData.topic} 
                onChange={e => setFormData({ ...formData, topic: e.target.value })}
              >
                <option value="">請選擇諮詢主題</option>
                {enhancedTopicOptions.map((opt, idx) => (
                  <option key={opt} value={opt}>{idx + 1}. {opt}</option>
                ))}
              </select>
            ) : (
              <input 
                type="text" 
                required
                className={inputClass} 
                placeholder="請簡述您的需求"
                value={formData.topic} 
                onChange={e => setFormData({ ...formData, topic: e.target.value })} 
              />
            )}
          </div>

          {/* 備註說明 */}
          <div className="space-y-1">
            <label className={labelClass}><FileText className="w-3.5 h-3.5 text-blue-500" /> 額外需求備註</label>
            <textarea 
              rows={4}
              className={`${inputClass} resize-none`} 
              placeholder="有任何其他需要我們注意的事項嗎？"
              value={formData.description} 
              onChange={e => setFormData({ ...formData, description: e.target.value })} 
            />
          </div>

          <div className="pt-6">
            <button 
              type="submit" 
              disabled={submitting}
              className="w-full bg-blue-600 text-white py-6 rounded-3xl font-black text-xl shadow-2xl shadow-blue-100 hover:bg-blue-700 active:scale-[0.98] transition-all flex items-center justify-center gap-3 disabled:opacity-50"
            >
              {submitting ? (
                <><Loader2 className="w-7 h-7 animate-spin" /><span>提交中...</span></>
              ) : (
                <><Send className="w-6 h-6" /><span>確認提交預約申請</span></>
              )}
            </button>
            <p className="text-center text-[10px] text-slate-400 font-black mt-8 uppercase tracking-[0.25em]">© Secure Reservation System · Powered by ConsultFlow</p>
          </div>
        </form>
      </div>
    </div>
  );
};
